import { createSlice } from '@reduxjs/toolkit';
import { DEFAULT_SELECTED_ALERT_SOURCES } from 'features/Weather/features/WeatherAlerts/config/alertSources.config';
import { getAlertIdentifier } from 'features/Weather/features/WeatherAlerts/utils/alertData.utils';
import { getAlertWithSeverityLabel } from 'features/Weather/features/WeatherAlerts/utils/alertSeverity.utils';
import { closeAlertThunk } from '../thunks/alertListThunks';

const alertListSlice = createSlice({
  name: 'alertList',
  initialState: {
    alertList: [],
    showCloseAlertModal: false, // whether to show the modal to close an alert
    listConfig: {
      pageIndex: 1,
      sortField: 'newest',
      showLocalOnly: false, // whether to only show alerts local to the user's current location
      maxDist: 50, // max distance from the user's current location
      searchTerm: '', // the current search term used to filter the alert list
      selectedSources: DEFAULT_SELECTED_ALERT_SOURCES,
      alertTypesBySource: {},
    },
    selectedAlertConfig: {
      selectedAlert: null, // the ID of the currently selected alert
      mapCenter: null, // the center coordinates for the map when this alert is selected
      mapPolygon: null, // the polygon coordinates for the map when this alert is selected
    },
  },
  reducers: {
    setAlertList: (state, action) => {
      state.alertList = Array.isArray(action.payload)
        ? action.payload.map(getAlertWithSeverityLabel)
        : [];
    },
    setListConfig: (state, action) => {
      state.listConfig = {
        ...state.listConfig,
        ...action.payload,
      };
    },
    setPageIndex: (state, action) => {
      state.listConfig.pageIndex = action.payload;
    },
    setSortField: (state, action) => {
      state.listConfig.sortField = action.payload;
    },
    setShowLocalOnly: (state, action) => {
      state.listConfig.showLocalOnly = action.payload;
    },
    setMaxDist: (state, action) => {
      state.listConfig.maxDist = action.payload;
    },
    setSearchTerm: (state, action) => {
      state.listConfig.searchTerm = action.payload;
    },
    setAlertTypesBySource: (state, action) => {
      state.listConfig.alertTypesBySource =
        action.payload && typeof action.payload === 'object' ? action.payload : {};
    },

    setSelectedAlertConfig: (state, action) => {
      state.selectedAlertConfig = {
        ...state.selectedAlertConfig,
        ...action.payload,
      };
    },
    setSelectedAlert: (state, action) => {
      state.selectedAlertConfig.selectedAlert = action.payload;
    },
    setMapCenter: (state, action) => {
      state.selectedAlertConfig.mapCenter = action.payload;
    },
    setMapPolygon: (state, action) => {
      state.selectedAlertConfig.mapPolygon = action.payload;
    },
    setShowCloseAlertModal: (state, action) => {
      state.showCloseAlertModal = !!action.payload; // ensure the value is a boolean
    },
    removeAlertById: (state, action) => {
      const alertId = action.payload;
      state.alertList = state.alertList.filter(
        (alert, index) => getAlertIdentifier(alert, index) !== alertId,
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(closeAlertThunk.fulfilled, (state, action) => {
      const { alertId } = action.payload;

      state.alertList = state.alertList.filter(
        (alert, index) => getAlertIdentifier(alert, index) !== alertId,
      );

      state.showCloseAlertModal = false;

      if (state.selectedAlertConfig.alertId === alertId) {
        state.selectedAlertConfig = {
          alertId: null,
          mapCenter: null,
          mapPolygon: null,
        };
      }
    });
  },
});

export const {
  setAlertList,
  setShowCloseAlertModal,
  setListConfig,
  setPageIndex,
  setSortField,
  setShowLocalOnly,
  setAlertTypesBySource,
  setMaxDist,
  setSelectedAlertConfig,
  setSelectedAlert,
  setMapCenter,
  setMapPolygon,
  setSearchTerm,
} = alertListSlice.actions;

export default alertListSlice.reducer;
